import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import { AppleSSOButton, GoogleSSOButton } from "@shared/components";
import { useFlippers } from "@shared/hooks";
import { useLocation, useNavigate, useParams } from "@shared/react-router-dom";
import {
  FormWrapper,
  NewToFullscriptSignUp,
  SignInForm,
  SignInWrapper,
} from "@unauthenticated/shared/components";
import { NewSSOErrorBox } from "@unauthenticated/shared/components/NewSSOErrorBox/NewSSOErrorBox";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { useTreatmentPlanTerm } from "@unauthenticated/shared/hooks/useTreatmentPlanTerm";
import {
  SignUpModalContainer,
  SignUpModalFooter,
  SignUpModalHeader,
  SignUpModalSubtitle,
  SignUpModalTitle,
  StoreLogo,
  StoreOffersPill,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./RxSignInModalMain.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  newSSOErrors?: string;
}

const RxSignInModalMain: FC<Props> = ({
  patientAttributes,
  additionalAttributes,
  newSSOErrors,
}) => {
  const treatmentPlanTerm = useTreatmentPlanTerm();
  const { t } = useTranslation();
  const {
    storeLogo,
    storeName,
    pracName,
    firstName,
    treatmentPlanSlug,
    uuid,
    labRecommendationId,
    storeSlug,
  } = patientAttributes;

  const { practitionerSlug, tpSlug } = useParams<"practitionerSlug" | "tpSlug">();
  const navigate = useNavigate();
  const searchParams = useLocation().search;
  const formVersion = "StoreSignupPage";
  const [isAppleSSO] = useFlippers("apple_sso");

  const SIGN_IN_PAGE = "rx_patient_sign_in_page";
  const PAGE_VERSION = "react_with_sso";

  const signupModalSubtitle = labRecommendationId
    ? t(l.patientRxAuth.SignInWelcomeMessage, {
        firstName,
        pracName,
      })
    : t(l.patientRxAuth.SignInWelcomeMessage, {
        firstName,
        treatmentPlanTerm,
        pracName,
      });

  const handleOnSignUpClick = () => {
    navigate(`${authRoutes.welcome_sign_up(storeSlug)}${searchParams}`);
  };

  const [isPatientStoreLandingPageEnabled] = useFlippers("titan_patient_store_landing_page");
  const onCompleted = completedData => {
    const { redirectPath } = completedData?.auth?.userSignIn;

    if (redirectPath) {
      window.location.assign(completedData.auth.userSignIn.redirectPath);
    }
  };

  return (
    <>
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        {!isPatientStoreLandingPageEnabled && (
          <SignUpModalSubtitle subtitle={signupModalSubtitle} />
        )}
      </SignUpModalHeader>
      <SignUpModalContainer isNoPaddingTop={isPatientStoreLandingPageEnabled}>
        <FormWrapper>
          <SignInWrapper>
            <NewSSOErrorBox newSSOErrors={newSSOErrors} />
            <GoogleSSOButton
              isOrTextRendered={!isAppleSSO}
              signUpPage={SIGN_IN_PAGE}
              pageVersion={PAGE_VERSION}
              treatmentPlanSlug={treatmentPlanSlug}
              labRecommendationId={labRecommendationId}
              storeSlug={storeSlug}
              uuid={uuid}
            />
            {isAppleSSO && (
              <AppleSSOButton
                css={styles.appleButton}
                isOrTextRendered
                signUpPage={SIGN_IN_PAGE}
                pageVersion={PAGE_VERSION}
                treatmentPlanSlug={treatmentPlanSlug}
                labRecommendationId={labRecommendationId}
                storeSlug={storeSlug}
                uuid={uuid}
              />
            )}
            <SignInForm
              additionalAttributes={{ formVersion, ...additionalAttributes }}
              forgotPasswordLink={
                isPatientStoreLandingPageEnabled
                  ? authRoutes.welcome_forgot_password(storeSlug, uuid || treatmentPlanSlug)
                  : authRoutes.rx_sign_in_forgot_password(practitionerSlug, tpSlug)
              }
              onCompleted={onCompleted}
            />
            <Spacer height="doubleHalf" />
            <NewToFullscriptSignUp
              userType="patient"
              customSignUpAction={isPatientStoreLandingPageEnabled && handleOnSignUpClick}
            />
          </SignInWrapper>
        </FormWrapper>
      </SignUpModalContainer>
      {!isPatientStoreLandingPageEnabled && <SignUpModalFooter />}
    </>
  );
};

export { RxSignInModalMain };
